/**
 * activityStore — in-memory ActivityStack (D1).
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decisions D1, D2.
 *
 * Mutations are pure: this store does NOT call navigate() or read from
 * react-router. The dispatcher (src/navigation/dispatchBack.ts) bridges
 * store mutations to React Router side effects.
 *
 * Freeze depth cap (D2):
 *   When pushEntry would exceed FREEZE_DEPTH_CAP entries in an activity,
 *   the oldest non-thawed entry is marked thawed (component subtree
 *   unmounted by the renderer; URL + scrollY retained).
 *   On pop, if the new top entry is thawed, it remains thawed and the
 *   renderer remounts it from URL.
 */

import { create } from "zustand";
import type {
  ActivityStackState,
  DialogState,
  OpenActivityArgs,
  PushEntryArgs,
  SubActivity,
  SubEntry,
} from "../navigation/types";

export const FREEZE_DEPTH_CAP = 3;

interface ActivityStoreActions {
  /**
   * Add a new SubActivity above the current top.
   * Returns the new activity's id.
   */
  openActivity: (args: OpenActivityArgs) => string;

  /**
   * Push an entry inside the current top activity.
   * No-op if there is no top activity (caller should openActivity first).
   * Applies D2 freeze cap.
   */
  pushEntry: (args: PushEntryArgs) => void;

  /**
   * Replace only the top entry of the current top activity.
   * Previous top entry is discarded (not frozen).
   */
  replaceEntry: (args: PushEntryArgs) => void;

  /**
   * Replace the entire current top activity with a new one.
   * Discards all entries of the previous top activity.
   */
  replaceActivity: (args: OpenActivityArgs) => string;

  /**
   * Close one SubActivity by id (default: top). Discards all its entries.
   */
  closeActivity: (activityId?: string) => void;

  /** Close every SubActivity and dialog. Used by terminal activity CTAs. */
  closeAll: () => void;

  /** Close every activity above the latest activity with this kind. */
  closeAboveKind: (kind: string) => void;

  /**
   * 구매 funnel 걷어내기 (제네릭) — top 부터 kind 가 funnelKinds 에 속하는 activity 를
   * 연속으로 닫고, 그 아래 첫 비-funnel activity(origin/상세)에서 멈춘다. 전부 funnel 이면
   * 스택을 비운다. 살아남은 origin activity 를 반환(없으면 null). 순수 mutation.
   *
   * origin 의 kind 를 명시하지 않으므로 쿠지(kuji/detail)·가챠 공통으로 쓴다.
   */
  closeFunnel: (funnelKinds: ReadonlySet<string>) => SubActivity | null;

  /**
   * Close the topmost activity (convenience for the dispatcher).
   * Equivalent to closeActivity() with no arg.
   */
  closeTop: () => void;

  /** Push a blocking dialog onto the stack. Returns the new dialog id. */
  openDialog: (kind: string) => string;

  /**
   * Close a dialog. If `dialogId` is omitted, pops the top dialog. If
   * specified, removes that dialog id from the stack (no-op if not found).
   */
  closeDialog: (dialogId?: string) => void;

  /** Save scroll position for a specific entry (called by renderer on scroll). */
  setEntryScroll: (
    activityId: string,
    entryId: string,
    scrollY: number,
  ) => void;

  /**
   * Pop the top entry of an activity. If the activity has only one entry,
   * the activity itself is closed. Used by the back dispatcher.
   */
  popEntry: (activityId: string) => void;

  /**
   * Reconcile store state to a URL, used after browser back / hardware back /
   * iOS swipe-back. Walks the stack top-down looking for an entry whose URL
   * matches; trims everything above it. If no entry matches, clears the stack.
   *
   * Pure mutation — no navigate() call. Avoids infinite loops with React Router.
   */
  reconcileFromUrl: (url: string) => void;

  /** Reset to empty state. Test-only / app-reset. */
  reset: () => void;
}

export type ActivityStoreState = ActivityStackState & ActivityStoreActions;

const initialState: ActivityStackState = {
  activities: [],
  dialogs: [],
};

let idCounter = 0;
function nextId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${idCounter}-${Date.now().toString(36)}`;
}

function makeEntry(args: PushEntryArgs): SubEntry {
  return {
    id: nextId("entry"),
    url: args.url,
    scrollY: 0,
    state: args.state ?? null,
    thawed: false,
  };
}

function makeActivity(args: OpenActivityArgs): SubActivity {
  return {
    id: nextId("activity"),
    kind: args.kind,
    presentation: args.presentation,
    appBarAction: args.appBarAction,
    fallbackPath: args.fallbackPath,
    historyIsolated: args.historyIsolated ?? false,
    entries: [makeEntry({ url: args.url, state: args.state })],
  };
}

/**
 * Apply D2 freeze cap to an entries array.
 * Marks the oldest non-thawed entry as thawed once length > cap.
 * Pure function — returns a new array.
 */
function applyFreezeCap(entries: SubEntry[]): SubEntry[] {
  if (entries.length <= FREEZE_DEPTH_CAP) return entries;

  // Count active (non-thawed) entries.
  const activeCount = entries.reduce(
    (n, e) => (e.thawed ? n : n + 1),
    0,
  );
  if (activeCount <= FREEZE_DEPTH_CAP) return entries;

  // Thaw the oldest non-thawed entry.
  const oldestActiveIdx = entries.findIndex((e) => !e.thawed);
  if (oldestActiveIdx === -1) return entries;

  return entries.map((e, i) =>
    i === oldestActiveIdx ? { ...e, thawed: true } : e,
  );
}

/**
 * Replace the top activity in `activities` using a transformation function.
 * If the result is null, the top activity is removed.
 * Pure helper — returns a new array.
 */
function withTopActivity(
  activities: SubActivity[],
  transform: (top: SubActivity) => SubActivity | null,
): SubActivity[] {
  if (activities.length === 0) return activities;
  const next = transform(activities[activities.length - 1]);
  if (next === null) return activities.slice(0, -1);
  return [...activities.slice(0, -1), next];
}

export const useActivityStore = create<ActivityStoreState>()((set, get) => ({
  ...initialState,

  openActivity: (args) => {
    const activity = makeActivity(args);
    set((s) => ({ activities: [...s.activities, activity] }));
    return activity.id;
  },

  pushEntry: (args) => {
    set((s) => ({
      activities: withTopActivity(s.activities, (top) => ({
        ...top,
        entries: applyFreezeCap([...top.entries, makeEntry(args)]),
      })),
    }));
  },

  replaceEntry: (args) => {
    set((s) => ({
      activities: withTopActivity(s.activities, (top) => {
        if (top.entries.length === 0) {
          return { ...top, entries: [makeEntry(args)] };
        }
        const replaced: SubEntry = {
          ...makeEntry(args),
          // Preserve the slot's id so renderers don't see it as a new mount.
          id: top.entries[top.entries.length - 1].id,
        };
        return {
          ...top,
          entries: [...top.entries.slice(0, -1), replaced],
        };
      }),
    }));
  },

  replaceActivity: (args) => {
    const activity = makeActivity(args);
    set((s) => {
      if (s.activities.length === 0) {
        return { activities: [activity] };
      }
      return { activities: [...s.activities.slice(0, -1), activity] };
    });
    return activity.id;
  },

  closeActivity: (activityId) => {
    set((s) => {
      if (s.activities.length === 0) return {};
      if (activityId === undefined) {
        return { activities: s.activities.slice(0, -1) };
      }
      return {
        activities: s.activities.filter((a) => a.id !== activityId),
      };
    });
  },

  closeAll: () => {
    set({ activities: [], dialogs: [] });
  },

  closeAboveKind: (kind) => {
    set((s) => {
      let idx = -1;
      for (let i = s.activities.length - 1; i >= 0; i -= 1) {
        if (s.activities[i].kind === kind) {
          idx = i;
          break;
        }
      }
      if (idx === -1) return {};
      return { activities: s.activities.slice(0, idx + 1) };
    });
  },

  closeFunnel: (funnelKinds) => {
    const s = get();
    // top 부터 funnel kind 를 건너뛰며 첫 비-funnel(origin) 인덱스를 찾는다.
    let originIdx = s.activities.length - 1;
    while (originIdx >= 0 && funnelKinds.has(s.activities[originIdx].kind)) {
      originIdx -= 1;
    }
    if (originIdx < 0) {
      // 스택 전체가 funnel — origin 없음. 전부 비우고 null 반환 (caller 가 fallback nav).
      if (s.activities.length > 0) set({ activities: [] });
      return null;
    }
    const origin = s.activities[originIdx];
    // 이미 top 이 origin 이면 닫을 funnel 없음 (no-op).
    if (originIdx < s.activities.length - 1) {
      set({ activities: s.activities.slice(0, originIdx + 1) });
    }
    return origin;
  },

  closeTop: () => {
    get().closeActivity();
  },

  openDialog: (kind) => {
    const dialog: DialogState = { id: nextId("dialog"), kind };
    set((s) => ({ dialogs: [...s.dialogs, dialog] }));
    return dialog.id;
  },

  closeDialog: (dialogId) => {
    set((s) => {
      if (s.dialogs.length === 0) return {};
      if (dialogId === undefined) {
        return { dialogs: s.dialogs.slice(0, -1) };
      }
      const next = s.dialogs.filter((d) => d.id !== dialogId);
      if (next.length === s.dialogs.length) return {};
      return { dialogs: next };
    });
  },

  setEntryScroll: (activityId, entryId, scrollY) => {
    set((s) => ({
      activities: s.activities.map((a) => {
        if (a.id !== activityId) return a;
        return {
          ...a,
          entries: a.entries.map((e) =>
            e.id === entryId ? { ...e, scrollY } : e,
          ),
        };
      }),
    }));
  },

  popEntry: (activityId) => {
    set((s) => {
      const idx = s.activities.findIndex((a) => a.id === activityId);
      if (idx === -1) return {};
      const target = s.activities[idx];
      if (target.entries.length <= 1) {
        // Single-entry activity: close it.
        return {
          activities: s.activities.filter((_, i) => i !== idx),
        };
      }
      const updated: SubActivity = {
        ...target,
        entries: target.entries.slice(0, -1),
      };
      return {
        activities: s.activities.map((a, i) => (i === idx ? updated : a)),
      };
    });
  },

  reconcileFromUrl: (url) => {
    set((s) => {
      if (s.activities.length === 0) return {};

      // Walk top-down looking for an entry that matches the URL.
      let foundActivityIdx = -1;
      let foundEntryIdx = -1;
      for (let ai = s.activities.length - 1; ai >= 0; ai--) {
        const activity = s.activities[ai];
        for (let ei = activity.entries.length - 1; ei >= 0; ei--) {
          if (activity.entries[ei].url === url) {
            foundActivityIdx = ai;
            foundEntryIdx = ei;
            break;
          }
        }
        if (foundActivityIdx !== -1) break;
      }

      if (foundActivityIdx === -1) {
        // URL is not in any activity — user popped past the whole stack.
        return { activities: [] };
      }

      // Trim activities above the matching one.
      const trimmedActivities = s.activities.slice(0, foundActivityIdx + 1);
      const matchingActivity = trimmedActivities[trimmedActivities.length - 1];
      // Trim entries above the matching one in that activity.
      const trimmedActivity: SubActivity = {
        ...matchingActivity,
        entries: matchingActivity.entries.slice(0, foundEntryIdx + 1),
      };
      return {
        activities: [...trimmedActivities.slice(0, -1), trimmedActivity],
      };
    });
  },

  reset: () => {
    set({ ...initialState });
  },
}));
